import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  PLATFORM_ID,
  Injector,
  ViewChild,
  afterNextRender,
  inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { ScheduleTasksComponent } from './components/schedule/schedule-tasks/schedule-tasks.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import {
  ActivatedRouteSnapshot,
  ActivationEnd,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Route,
  Router,
  RouterModule,
} from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { filter, take } from 'rxjs';

import { UserService } from './shared/services/user.service';
import { readCallbackError, withoutCallbackParams } from './core/session/login-callback';
import { ToastService } from './services/toast.service';
import { VersionService } from './services/version.service';
import { AppCommunicationService } from './shared/services/app-communication.service';
import { SharedServices } from './shared/services/shared.service';
import { GoogleMapsLoaderService } from './shared/services/google-maps-loader.service';
import { SplashScreenComponent } from './components/layout/splash-screen/splash-screen.component';
import { UserMenuComponent } from './components/user/user-menu/user-menu.component';
import { NotificationsComponent } from './components/notifications/notifications/notifications.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { BreakpointObserver } from '@angular/cdk/layout';
import { FirebaseService } from './shared/services/firebase.service';
import { BackBarComponent } from './shared/components/back-bar/back-bar.component';

/** Primary navigation groups, in menu order. `group` matches route `data.group`; `icon` is a file in assets/icons/menu. */
export interface NavGroup {
  group: string;
  label: string;
  icon: string;
}

export const NAV_GROUPS: NavGroup[] = [
  { group: 'dashboard', label: 'Dashboard', icon: 'dashboard.svg' },
  { group: 'contractor', label: 'Prestadores', icon: 'Prestadores.svg' },
  { group: 'client', label: 'Clientes', icon: 'Vector.svg' },
  { group: 'tasks', label: 'Calendario', icon: 'Calendario.svg' },
  { group: 'marketing', label: 'Marketing', icon: 'Marketing_new.svg' },
  { group: 'franchise', label: 'Franquicias', icon: 'franchises.svg' },
  { group: 'reports', label: 'Reportes', icon: 'Reportes.svg' },
  { group: 'budgets', label: 'Cotizaciones', icon: 'Cotizaciones.svg' },
  { group: 'scores', label: 'Score', icon: 'score-icon.svg' },
  { group: 'setting', label: 'Configuración', icon: 'Configuraciones_new.svg' },
];

/** Below this width the primary menu moves into the side drawer opened by the hamburger button. */
const COMPACT_NAV_QUERY = '(max-width: 1023.98px)';

/**
 * Deep-linked pages (route `data.deepLink`) that cannot open without the record id in `?id=`: opened bare they
 * rendered empty and fired API errors. `clients/clientDetail` is not listed: without an id it is the new-client form.
 */
export const DEEP_LINKS_REQUIRING_ID = [
  'clients/clientDetailView',
  'clients/clientIdentificationNumber',
  'contractor-scores/supplier-score',
];

/** True when `snapshot` is one of DEEP_LINKS_REQUIRING_ID opened without its `?id=`. */
export function isDeepLinkMissingId(snapshot: ActivatedRouteSnapshot): boolean {
  if (!snapshot.data?.['deepLink']) return false;
  const path = snapshot.pathFromRoot
    .map((s) => s.routeConfig?.path)
    .filter((p) => !!p)
    .join('/');
  return DEEP_LINKS_REQUIRING_ID.includes(path) && !snapshot.queryParamMap.get('id');
}

/** A group as listed in the mobile drawer: its visible sub-sections, already permission-filtered. */
interface DrawerSection extends NavGroup {
  items: Route[];
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    MatSidenavModule,
    MatDialogModule,
    SplashScreenComponent,
    UserMenuComponent,
    NotificationsComponent,
    MatButtonModule,
    MatIconModule,
    BackBarComponent,
  ],
})
export class AppComponent implements OnInit, AfterViewInit {
  toggledSidebar: boolean = true;
  selected: string = '';
  selectedGroup: string = '';
  @ViewChild('drawer') drawer!: MatSidenav;
  @ViewChild('subbarTabs') subbarTabs?: ElementRef<HTMLElement>;

  readonly navGroups = NAV_GROUPS;
  /** True below 1024px: the top bar shows the hamburger instead of the inline menu. */
  isCompactNav = false;
  navDrawerOpen = false;
  /** Drawer content, rebuilt every time it opens so it reflects the current permissions. */
  drawerSections: DrawerSection[] = [];
  /** Group expanded in the drawer (one at a time; starts on the current group). */
  expandedDrawerGroup = '';

  /** On a detail page (route `data.menu.pathParent`), the sub-tab of its parent list, highlighted as the current one. */
  parentTabPath = '';

  breadcrumbList: Array<any> = [];
  /**
   * Last first-level page (with its query string) visited in each menu group. Clicking a group went to its FIRST
   * page every time: "Calendario" always loaded the task calendar (12 requests) even to get back to "Orden de montaje".
   */
  private readonly lastPageByGroup = new Map<string, string>();
  showHeader: boolean = false;
  routeData: any;
  secondaryMenu: any[] = [];
  firstNav: any;
  displayOpenFilterButton: boolean = false;

  exceptionRoutes: string[] = [
    'workload',
    'ManufacturingOrder',
    'SapArticles',
    'scheduleTasks',
  ];

  headerlessRoutes: string[] = [
    '/tp-terms',
    '/privacy',
    '/login',
    '/service-request',
  ];

  @ViewChild(ScheduleTasksComponent, { static: false })
  scheduleTasksFilterComponent!: ScheduleTasksComponent;

  userSvc = inject(UserService);
  router = inject(Router);
  versionSrv = inject(VersionService);
  dialog = inject(MatDialog);
  appCommunicationService = inject(AppCommunicationService);
  sharedService = inject(SharedServices);
  gMapsLoader = inject(GoogleMapsLoaderService);
  firebaseSvc = inject(FirebaseService);

  private readonly platformId = inject(PLATFORM_ID);
  private readonly injector = inject(Injector);

  constructor() {
    this.dropLoginCallbackParams();
    inject(BreakpointObserver)
      .observe(COMPACT_NAV_QUERY)
      .subscribe((state) => {
        this.isCompactNav = state.matches;
        if (!state.matches) this.navDrawerOpen = false;
      });
    this.appCommunicationService.executedisplayOpenFilterButton$.subscribe(
      (value: boolean) => {
        setTimeout(() => {
          this.displayOpenFilterButton = value;
        });
      }
    );
  }

  ngOnInit(): void {
    this.ngOnInitAsync();
  }

  /**
   * Auth0 comes back to `origin/?code=…&state=…#/…`. The router lives in the hash, so nothing ever removed that query
   * string: every reload (F5) re-ran the login callback and sent the user to /welcome, whatever page they were on.
   * Once Auth0 has handled the callback, drop its parameters from the address bar (same entry, hash untouched). When
   * Auth0 sent the user back with an error (blocked account, access denied…) it is explained: the login page alone
   * would show nothing.
   */
  private dropLoginCallbackParams() {
    const toast = inject(ToastService);
    inject(AuthService)
      .isLoading$.pipe(
        filter((loading) => !loading),
        take(1)
      )
      .subscribe(() => {
        const url = new URL(window.location.href);
        const failure = readCallbackError(url);
        if (failure) toast.error(failure.title, failure.message);

        const clean = withoutCallbackParams(url);
        if (clean) history.replaceState(history.state, '', clean);
      });
  }

  private async ngOnInitAsync(): Promise<void> {
    // Activamos el listener para que espere a que te loguees con el popup
    this.firebaseSvc.initListener();

    // Inicializo mapa de Google
    this.userSvc.isLoggedInChanged.subscribe((isLogged: boolean) => {
      // Verificamos que sea el navegador y que el usuario esté logueado
      if (isLogged && isPlatformBrowser(this.platformId)) {

        // S7764: Usamos globalThis en lugar de window
        const global = globalThis as any;

        if ('requestIdleCallback' in global) {
          // Prioridad baja: se ejecuta cuando el navegador deja de estar ocupado
          global.requestIdleCallback(() => this.loadGoogleMaps());
        } else {
          // Fallback para navegadores antiguos (o entornos donde no esté disponible)
          global.setTimeout(() => this.loadGoogleMaps(), 100);
        }
      }
    });

    // Inicializo routeData
    this.routeData = this.router.config.filter((x) => x.data && x.data.group);

    // Subscribo eventos de router
    this.router.events.subscribe((event: any) => {
      this.breadcrumb(event);

      if (event instanceof NavigationEnd) {
        this.navDrawerOpen = false;
        this.rememberPage(event.urlAfterRedirects);
        this.selected = event.url.replace('/', '');
        this.routeData = this.router.config.filter(
          (x) => x.data && x.data.group
        );

        // Construyo sub menu
        this.buildSecondaryMenu(this.selected);
        this.scrollActiveTabIntoView();
      }

      // When a guard (unsaved changes) or an error stops a navigation, the menu stays on the page the user is still on.
      if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.selected = this.router.url.replace('/', '');
        this.buildSecondaryMenu(this.selected);
      }
    });

    // Si arranco con una ruta especifica, construyo menu al inicio
    if (this.router.url) {
      this.selected = this.router.url.replace('/', '');
      this.buildSecondaryMenu(this.selected);
    }
  }

  private async loadGoogleMaps() {
    try {
      await this.gMapsLoader.load();
    } catch (error) {
      console.error('Error cargando Maps:', error);
    }
  }

  private buildSecondaryMenu(selectedPath: string) {
    // A mistyped URL (#/homeX, #/clientList/foo) is the 404 page: it belongs to no group. Matching it by its first
    // segment or by prefix sent it to that group's first page instead.
    const group = this.isNotFoundPage() ? undefined : this.findGroup(selectedPath);
    this.parentTabPath = group?.data?.menu?.pathParent ?? '';

    if (!group) {
      // A page outside the menu (profile, welcome…): no top item stays highlighted from the previous page.
      this.secondaryMenu = [];
      this.selectedGroup = '';
      return;
    }

    // The highlighted top item is always the group that owns the current route.
    this.selectedGroup = group.data.group;

    // Ruta de excepcion
    if (this.exceptionRoutes.includes(group.path)) {
      this.secondaryMenu = this.routeData
        .filter((r: Route) => r.data && r.data.group === group.data.group)
        .filter(
          (r: Route) =>
            r.data && this.userSvc.hasPermissions(r.data.permissions)
        )
        .filter(
          (r: Route) =>
            r.data && r.data.breadcrumb && r.data.breadcrumb.level === 1
        );

      if (
        !this.secondaryMenu.find((x) => x.path === this.selected) &&
        this.secondaryMenu.length > 0
      ) {
        this.selected = this.secondaryMenu[0].path;
      }

      return;
    }

    // Rutas normales (que no son de excepcion)
    if (!group.data.menu.pathParent) {
      this.filterMenuOptions(group.data.group);

      this.checkSelectedGroup(group.data.group);
    } else {
      // Detail page (client history, supplier detail…): show its own group's tabs without redirecting, instead of
      // keeping the tabs of whatever group was visited before.
      this.secondaryMenu = this.subSectionsOf(group.data.group);
    }
  }

  /** True while the router shows the wildcard (`**`) route, i.e. the 404 page. */
  private isNotFoundPage(): boolean {
    return this.router.routerState.snapshot.root.firstChild?.routeConfig?.path === '**';
  }

  /**
   * Below ~1000px the sub-tabs scroll horizontally; after a navigation the current tab could be out of view
   * (Orden de montaje, ABM Tareas, Beneficios at 390px). Center it in the row once the menu has rendered.
   */
  private scrollActiveTabIntoView() {
    // Wait for the next render so the tabs of a newly selected group exist. Find the tab by its path rather than by the
    // active class: RouterLinkActive on freshly created tabs only marks them later, which left the row unscrolled when
    // the user switched groups or opened a page directly.
    const path = this.parentTabPath || this.router.url.replace(/^\//, '').split(/[?#]/)[0];
    afterNextRender(
      {
        read: () => {
          const nav = this.subbarTabs?.nativeElement;
          const tab =
            nav?.querySelector<HTMLElement>(`.tp-subbar__tab[data-path="${CSS.escape(path)}"]`) ??
            nav?.querySelector<HTMLElement>('.tp-subbar__tab--active');
          if (!nav || !tab || nav.scrollWidth <= nav.clientWidth) return;
          const navBox = nav.getBoundingClientRect();
          const tabBox = tab.getBoundingClientRect();
          nav.scrollLeft += tabBox.left - navBox.left - (navBox.width - tabBox.width) / 2;
        },
      },
      { injector: this.injector }
    );
  }

  ngAfterViewInit(): void {
    this.versionSrv.checkForUpdates();
  }

  closeDrawer() {
    this.toggledSidebar = false;
  }

  filterMenuOptions(group: string) {
    const options = this.routeData?.filter(
      (x: {
        data: {
          breadcrumb: { level: number; description: string };
          group: string;
          permissions: string[];
        };
      }) =>
        x.data?.group == group &&
        this.userSvc.hasPermissions(x.data?.permissions) &&
        x.data.breadcrumb.level == 1
    );

    // Compare the path only: a list that keeps its state in the query string (?q=&page=) is still the selected page,
    // otherwise every NavigationEnd sent it back to the bare URL and dropped that state.
    const selectedPath = this.selected.split('?')[0];
    const firstNav = options.find((x: Route) => x.path == selectedPath);
    if (!firstNav && options.length > 0) {
      const remembered = this.lastPageByGroup.get(group);
      const rememberedPath = remembered?.replace(/^\//, '').split('?')[0];
      const target = remembered && options.some((x: Route) => x.path == rememberedPath)
        ? remembered
        : '/' + options[0].path;
      // The top bar and the tabs follow the navigation (NavigationEnd rebuilds them): while a guard asks
      // "¿Salir sin guardar?" they keep showing the page the user is still on.
      this.router.navigateByUrl(target);
      return;
    }
    this.secondaryMenu = options;
    this.firstNav = firstNav;
    this.selectedGroup = group;
  }

  private rememberPage(url: string) {
    const path = url.replace(/^\//, '').split('?')[0].split('#')[0];
    const route = this.routeData?.find((x: Route) => x.path === path);
    if (route?.data?.breadcrumb?.level === 1 && route.data.group) {
      this.lastPageByGroup.set(route.data.group, url);
    }
  }

  toggleVisibility() {
    this.appCommunicationService.toggleVisibility();
  }

  toggleTagVisibility() {
    this.appCommunicationService.toggleTagVisibility();
  }

  findGroup(path: string) {
    const cleanPath = path.split('?')[0].split('#')[0];
    const firstSegment = cleanPath.split('/')[0];
    // Exact first segment only: the old prefix fallback put #/homeX in "home" and #/levelsfoo in "levels".
    return this.routeData?.find((x: { path: string }) => x.path === firstSegment);
  }

  checkHasAnyPermission(group: string) {
    return (
      this.routeData?.filter(
        (x: {
          data: {
            breadcrumb: any;
            group: string;
            permissions: string[];
          };
        }) =>
          x.data?.group == group &&
          this.userSvc.hasPermissions(x.data?.permissions)
      ).length > 0
    );
  }

  /** Sub-sections of a group shown in the tab row and in the drawer: same filter as filterMenuOptions. */
  subSectionsOf(group: string): Route[] {
    return (
      this.routeData?.filter(
        (x: Route) =>
          x.data?.['group'] == group &&
          this.userSvc.hasPermissions(x.data?.['permissions']) &&
          x.data?.['breadcrumb']?.level == 1 &&
          x.data?.['menu']?.description
      ) ?? []
    );
  }

  openNavDrawer() {
    this.drawerSections = this.navGroups
      .filter((g) => this.checkHasAnyPermission(g.group))
      .map((g) => ({ ...g, items: this.subSectionsOf(g.group) }));
    this.expandedDrawerGroup = this.selectedGroup;
    this.navDrawerOpen = true;
  }

  toggleDrawerGroup(section: DrawerSection) {
    if (section.items.length === 0) {
      // Nothing to expand: behave like the top-bar item (navigate to the group's page).
      this.filterMenuOptions(section.group);
      this.navDrawerOpen = false;
      return;
    }
    this.expandedDrawerGroup = this.expandedDrawerGroup === section.group ? '' : section.group;
  }

  checkSelectedGroup(group: string) {
    return this.selectedGroup == group ? 'selected-group' : '';
  }

  closeFranchise() {
    this.sharedService.selectedFranchise = undefined;
  }

  getDisplayName(name: string): string {
    const indusplastIndex = name.toLowerCase().indexOf('indusplast');
    if (indusplastIndex !== -1) {
      return name.substring(indusplastIndex + 'indusplast'.length).trim();
    }
    return name;
  }

  breadcrumb(router: any) {
    // A deep-linked detail opened without its record id (typed URL, old bookmark) goes to its list instead of
    // rendering an empty page with API errors.
    if (router instanceof ActivationEnd && isDeepLinkMissingId(router.snapshot)) {
      const parent = router.snapshot.data?.['menu']?.pathParent ?? 'home';
      this.router.navigate(['/' + parent], { replaceUrl: true });
      return;
    }

    if (router.snapshot?.data?.breadcrumb) {
      let breadcrumb = router.snapshot.data.breadcrumb;

      // A detail page opened directly (reload, link, new tab) used to be sent to /home. Pages that rebuild
      // themselves from the URL (data.deepLink) stay; the rest go to their own list instead of an unrelated screen.
      // Only the deepest route decides: activation events run parent first, and a parent without the flag (campaigns)
      // would otherwise send its deep-linkable child (campaigns/:id/report) away before the child is seen.
      if (
        this.breadcrumbList.length == 0 &&
        breadcrumb.level > 1 &&
        !router.snapshot.firstChild &&
        !router.snapshot.data?.deepLink
      ) {
        // Replace the entry: Back from the list must not reopen the empty detail.
        this.router.navigate(['/' + (router.snapshot.data?.menu?.pathParent ?? 'home')], { replaceUrl: true });
      }

      if (this.breadcrumbList.length > 1 && breadcrumb.level == 1) {
        this.breadcrumbList = [];
      } else {
        let maxLevel = Math.max(...this.breadcrumbList.map((o) => o.level));
        if (maxLevel >= breadcrumb.level) {
          this.breadcrumbList.forEach((el, index) => {
            if (
              el.level >= breadcrumb.level &&
              el.name != breadcrumb.description
            ) {
              this.breadcrumbList.splice(index, 1);
            }
          });
        } else {
          this.breadcrumbList.push({
            name: breadcrumb.description,
            level: breadcrumb.level,
            path: router.snapshot.url[0].path,
          });
        }
      }
    }
  }

  modifyHeader() {
    const cleanUrl = this.router.url.split('?')[0];
    this.showHeader = !this.headerlessRoutes.includes(cleanUrl);
  }
}
